import { type FollowUpOutcomeKind, type FollowUpView, renderFollowUpText } from '@sigora/domain';
import { useEffect, useRef } from 'react';

import { Pending } from '../../ui/Pending.tsx';
import {
  FOLLOW_UP_ANSWERING,
  FOLLOW_UP_CHECK_AGAIN_LABEL,
  FOLLOW_UP_FAILED,
  FOLLOW_UP_RETRY_LABEL,
  FOLLOW_UP_RETRY_WAITS,
  FOLLOW_UP_STILL_WORKING,
  FOLLOW_UP_UPDATE_LOST,
  FOLLOW_UP_UPDATING,
  followUpFailureLine,
  followUpFinalizedLine,
  followUpUpdateFailedLine,
} from '../brief-copy.ts';
import { type NoteCard, NoteCardPicker } from './NoteCardPicker.tsx';
import { OutcomeChoice } from './OutcomeChoice.tsx';

/**
 * The card a note from this follow-up would go under without asking: the card it was asked about,
 * else the one its answer relates to.
 */
export const noteTargetOf = (followUp: FollowUpView): string | null =>
  followUp.rqId ?? followUp.answer?.relates_to_rq_id ?? null;

export interface FollowUpAnswerProps {
  followUp: FollowUpView;
  /** The research question of a card by its id, or `null` when the Brief on hand has none. */
  cardText: (rqId: string) => string | null;
  /** The Brief's cards, for a note that names none; `null` when they could not be read. */
  cards: readonly NoteCard[] | null;
  /** Whether a choice or a Try again for this follow-up is on its way. */
  busy: boolean;
  /** Whether the thread's wait on this follow-up ran out while it was still answering or updating. */
  waitedOut: boolean;
  /** Whether another question is being answered, which holds this one's Try again until it is. */
  answeringElsewhere: boolean;
  /** Why the last choice or Try again did not go through, or `null`. */
  actionError: string | null;
  /** Whether the card picker is open for this follow-up's note. */
  picking: boolean;
  /** Whether this tab has just chosen the outcome, which moves focus to the line that says it. */
  focusOutcome: boolean;
  onChoose: (kind: FollowUpOutcomeKind, rqId: string | null) => void;
  onRetry: () => void;
  onCheckAgain: () => void;
  onCancelPick: () => void;
}

/**
 * A follow-up where a reply would be: Sigora answering, the reason there is no answer and Try
 * again, or the answer with its quotes and, under it, how it lands — the three outcomes while it
 * waits for one, a pending line while the Brief is being updated, and the outcome once it has one.
 */
export const FollowUpAnswer = ({
  followUp,
  cardText,
  cards,
  busy,
  waitedOut,
  answeringElsewhere,
  actionError,
  picking,
  focusOutcome,
  onChoose,
  onRetry,
  onCheckAgain,
  onCancelPick,
}: FollowUpAnswerProps) => {
  const outcomeLine = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (focusOutcome) {
      outcomeLine.current?.focus();
    }
  }, [focusOutcome]);

  const target = noteTargetOf(followUp);
  const card = target === null ? null : cardText(target);
  const { answer } = followUp;

  /**
   * Said when a wait ran out. An answer the server says was lost (`retryable`) offers Try again,
   * which asks for it anew. Anything else offers Check again, which only reads the thread again and
   * waits once more: an update whose job the queue still holds may be slow rather than lost, so it
   * is not sent again from here. An update whose job is gone (`updateRetryable`) offers Try again in
   * place of its pending line, whatever its wait.
   */
  const stillWorking = waitedOut && (
    <div className="follow-up-actions">
      <p>{FOLLOW_UP_STILL_WORKING}</p>
      {followUp.state === 'answering' && followUp.retryable ? (
        <button type="button" data-test-id="follow-up-retry" disabled={busy} onClick={onRetry}>
          {FOLLOW_UP_RETRY_LABEL}
        </button>
      ) : (
        <button type="button" data-test-id="follow-up-check-again" onClick={onCheckAgain}>
          {FOLLOW_UP_CHECK_AGAIN_LABEL}
        </button>
      )}
    </div>
  );

  return (
    <div className="follow-up" data-test-id="follow-up" data-state={followUp.state}>
      {followUp.state === 'answering' && (
        <>
          <Pending className="chat-activity" testId="follow-up-answering">
            {FOLLOW_UP_ANSWERING}
          </Pending>
          {stillWorking}
        </>
      )}

      {followUp.state === 'answer_failed' && (
        <div className="follow-up-actions">
          <p data-test-id="follow-up-failure" data-reason={followUp.answerReason ?? ''}>
            {followUp.answerReason === null
              ? FOLLOW_UP_FAILED
              : followUpFailureLine(followUp.answerReason)}
          </p>
          {answeringElsewhere && (
            <p data-test-id="follow-up-retry-waits">{FOLLOW_UP_RETRY_WAITS}</p>
          )}
          <button
            type="button"
            data-test-id="follow-up-retry"
            disabled={busy || answeringElsewhere}
            onClick={onRetry}
          >
            {FOLLOW_UP_RETRY_LABEL}
          </button>
        </div>
      )}

      {answer !== null && (
        <>
          <p data-test-id="follow-up-answer-text">{renderFollowUpText(answer.text)}</p>
          {answer.quotes.map((quote) => (
            <blockquote key={quote.quote_id} className="brief-quote" data-test-id="follow-up-quote">
              <p>{quote.text}</p>
              <footer className="brief-meta">{quote.participant}</footer>
            </blockquote>
          ))}
        </>
      )}

      {answer !== null && followUp.state === 'choosing' && (
        <>
          {followUp.updateReason !== null && (
            <p data-test-id="follow-up-update-failed" data-reason={followUp.updateReason}>
              {followUpUpdateFailedLine(followUp.updateReason)}
            </p>
          )}
          {picking ? (
            <NoteCardPicker
              cards={cards}
              busy={busy}
              onPick={(rqId) => onChoose('note', rqId)}
              onCancel={onCancelPick}
            />
          ) : (
            <OutcomeChoice
              card={card}
              suggested={answer.suggestion}
              busy={busy}
              updateFailed={followUp.updateReason !== null}
              onChoose={(kind) => onChoose(kind, null)}
            />
          )}
        </>
      )}

      {followUp.state === 'updating' &&
        (followUp.updateRetryable ? (
          <div className="follow-up-actions">
            <p data-test-id="follow-up-update-lost">{FOLLOW_UP_UPDATE_LOST}</p>
            <button
              type="button"
              data-test-id="follow-up-retry"
              disabled={busy}
              onClick={() => onChoose('update_brief', null)}
            >
              {FOLLOW_UP_RETRY_LABEL}
            </button>
          </div>
        ) : (
          <>
            <Pending className="chat-activity" testId="follow-up-updating">
              {FOLLOW_UP_UPDATING}
            </Pending>
            {stillWorking}
          </>
        ))}

      {followUp.outcome !== null && (
        <p
          ref={outcomeLine}
          tabIndex={-1}
          className="follow-up-outcome"
          data-test-id="follow-up-outcome"
          data-kind={followUp.outcome.kind}
        >
          {followUpFinalizedLine(
            followUp.outcome,
            followUp.outcome.kind === 'note' ? cardText(followUp.outcome.rqId) : null,
          )}
        </p>
      )}

      {actionError !== null && (
        <p className="chat-error" data-test-id="follow-up-error" role="alert">
          {actionError}
        </p>
      )}
    </div>
  );
};
